import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaUsers, FaVideo, FaWallet } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newAccount, setNewAccount] = useState({
    upiId: "",
    qrCode: null,
    bankName: "",
    accountNumber: "",
    ifsc: "",
    type: "upi",
  });
  const [tab, setTab] = useState("payment");
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchAccounts = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/payment-accounts`);
    setPaymentAccounts(res.data.accounts);
    const active = res.data.accounts.find((a) => a.isActive);
    setCurrentAccount(active);
  };

  const fetchUsers = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/users`);
    setUsers(res.data);
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/videos`);
      setVideos(response.data.videos); // <- Ensure this key matches your backend response
    } catch (err) {
      console.error("Failed to fetch videos:", err);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
    fetchVideos();
  }, []);

  const handleActivate = async (e) => {
    e.preventDefault();
    console.log("Selected Account ID:", selectedAccountId);
    await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/payment-accounts/activate`, {
      accountId: selectedAccountId,
    });
    fetchAccounts();
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("type", newAccount.type);

    if (newAccount.type === "upi") {
      formData.append("upiId", newAccount.upiId);
      if (newAccount.qrCode) {
        formData.append("qrCode", newAccount.qrCode);
      }
    } else {
      formData.append("bankName", newAccount.bankName);
      formData.append("accountNumber", newAccount.accountNumber);
      formData.append("ifsc", newAccount.ifsc);
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/payment-accounts`, formData);
      setNewAccount({
        upiId: "",
        qrCode: null,
        bankName: "",
        accountNumber: "",
        ifsc: "",
        type: "upi",
      });
      fetchAccounts();
    } catch (err) {
      console.error("Add account failed:", err.message);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm("Are you sure you want to delete this account?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/payment-accounts/${id}`);
      fetchAccounts();
    } catch (error) {
      console.error("Error deleting account:", error.response?.data || error.message);
    }
  };

  const handleBanUser = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/ban-user/${id}`);
    fetchUsers();
  };

  const handleDeleteUser = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/delete-user/${id}`);
    fetchUsers();
  };

  const handleDeleteVideo = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/videos/${id}`);
    fetchVideos();
  };
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
  };

  const fetchActiveAccount = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment-accounts/active`);
      setCurrentAccount(res.data.account);
    } catch {
      setCurrentAccount(null);
    }
  };

  const handleSetActive = async (id) => {
    await axios.put(`${process.env.REACT_APP_API_URL}/api/payment-accounts/activate`, {
      accountId: id,
    });
    fetchAccounts();
    fetchActiveAccount();
  };

  const totalUsers = users.length;
  const totalVideos = videos.length;
  const totalMembers = users.filter((u) => u.role !== "admin").length;

  return (
    <div className={`d-flex ${darkMode ? "bg-dark text-white" : "bg-light"}`}>
      {/* SIDEBAR */}
      <div
        className={`bg-dark text-white p-3 ${sidebarCollapsed ? "d-none" : "d-block"}`}
        style={{ width: "240px" }}
      >
        <h4 className="text-center mb-4">Admin Panel</h4>
        <ul className="nav flex-column">
          <li className="nav-item mb-2">
            <button onClick={() => setTab("users")} className={`btn btn-block text-left text-white ${tab === "users" ? "fw-bold" : ""}`}>
              <FaUsers className="me-2" /> Manage Users
            </button>
          </li>
          <li className="nav-item mb-2">
            <button onClick={() => setTab("videos")} className={`btn btn-block text-left text-white ${tab === "videos" ? "fw-bold" : ""}`}>
              <FaVideo className="me-2" /> Manage Videos
            </button>
          </li>
          <li className="nav-item mb-2">
            <button onClick={() => setTab("payment")} className={`btn btn-block text-left text-white ${tab === "payment" ? "fw-bold" : ""}`}>
              <FaWallet className="me-2" /> Payment Settings
            </button>
          </li>
        </ul>
      </div>

      {/* MAIN PANEL */}
      <div className="flex-grow-1 p-4">
        {/* TOP BAR */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button className="btn btn-outline-secondary" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <i className="bi bi-list"></i>
          </button>
          <div className="d-flex align-items-center gap-3">
            <button onClick={() => setDarkMode(!darkMode)} className="btn btn-outline-secondary">
              <i className={`bi ${darkMode ? "bi-brightness-high" : "bi-moon"}`}></i>
            </button>
            <div className="dropdown">
              <button className="btn btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown">
                <i className="bi bi-bell"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><span className="dropdown-item">No new notifications</span></li>
              </ul>
            </div>
            <img src="https://i.pravatar.cc/40" alt="avatar" className="rounded-circle" />
            <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <h4 className="page-title mb-3">Admin Dashboard</h4>
        <div className="row">
          <div className="col-md-4"><div className="card"><div className="card-body"><h6>Total Users Active</h6><h3>{totalUsers}</h3></div></div></div>
          <div className="col-md-4"><div className="card"><div className="card-body"><h6>Total Videos</h6><h3>{totalVideos}</h3></div></div></div>
          <div className="col-md-4"><div className="card"><div className="card-body"><h6>Members Subscribed</h6><h3>{totalMembers}</h3></div></div></div>
        </div>

        <div className="tab-content py-4">
          {/* USERS TAB */}
          {tab === "users" && (
            <div className="tab-pane active">
              <table className="table table-bordered table-striped">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>
                        <button className="btn btn-warning btn-sm me-2" onClick={() => handleBanUser(user._id)}>Ban</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(user._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* VIDEOS TAB */}
          {tab === "videos" && (
  <div className="tab-pane active">
    <input
      type="text"
      placeholder="Search by title..."
      className="form-control mb-3"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
    />

    {videos
      .filter((video) =>
        video.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((video) => (
        <div className="card2 mb-3" key={video._id}>
          <div className="card-body">
            <h5>{video.title}</h5>

            {/* Display thumbnail if available */}
            {video.thumbnail && (
              <img
                src={`${process.env.REACT_APP_API_URL}${video.thumbnail}`}
                alt="Thumbnail"
                width="150"
                className="img-thumbnail mb-2"
              />
            )}

            {/* Display video */}
            <video width="100%" height="240" controls>
              <source
                src={`${process.env.REACT_APP_API_URL}${video.videoUrl}`}
                type="video/mp4"
              />
            </video>

            <p className="mt-2">
              <strong>Visibility:</strong> {video.visibility}
            </p>
            <p>
              <strong>Creator:</strong> {video.creator?.name || "Unknown"}
            </p>

            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleDeleteVideo(video._id)}
            >
              Delete
            </button>
          </div>
        </div>
      ))}

    {/* Optional: Show message if no video matches search */}
    {videos.filter((v) =>
      v.title.toLowerCase().includes(searchTerm.toLowerCase())
    ).length === 0 && (
      <p className="text-muted">No videos match your search.</p>
    )}
  </div>
)}


          {/* PAYMENT TAB */} 
          {tab === "payment" && (
            <div className="tab-pane active">
              <div className="card-account">
                <div className="card-body">
                  <h5>Active Payment Method:</h5>
                  {currentAccount ? (
                    <>
                      <p><strong>Type:</strong> {currentAccount.type === "bank" ? "Bank Account" : "UPI"}</p>
                      {currentAccount.type === "upi" ? (
                        <>
                          <p><strong>UPI ID:</strong> {currentAccount.upiId}</p>
                          {currentAccount.qrCodeUrl && <img src={`${process.env.REACT_APP_API_URL}${currentAccount.qrCodeUrl}`} alt="QR Code" width="150" className="img-thumbnail" />}
                        </>
                      ) : (
                        <>
                          <p><strong>Bank:</strong> {currentAccount.bankName}</p>
                          <p><strong>Account No:</strong> {currentAccount.accountNumber}</p>
                          <p><strong>IFSC:</strong> {currentAccount.ifsc}</p>
                        </>
                      )}
                    </>
                  ) : <p>No active account set.</p>}

                  <hr />

                  <form onSubmit={handleActivate} className="row g-3 align-items-center">
                    <div className="col-auto"><label className="col-form-label">Change Active:</label></div>
                    <div className="col-auto">
                      <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)} required className="form-select">
                        <option value="">Select Account</option>
                        {paymentAccounts.map((acc) => (
                          <option key={acc._id} value={acc._id}>
                            {acc.type === "upi"
                              ? acc.upiId || "Unnamed UPI"
                              : `${acc.bankName || "Unknown Bank"} (${acc.accountNumber || "XXXX"})`}
                          </option>
                        ))}

                      </select>
                    </div>
                    <div className="col-auto"><button type="submit" className="btn btn-primary" >Set Active</button></div>
                    {selectedAccountId && (
                      <div className="col-auto">
                        <button type="button" onClick={() => handleDeleteAccount(selectedAccountId)} className="btn btn-danger">Delete</button>
                      </div>
                    )}
                  </form>

                  <hr />

                  <div className="d-flex justify-content-between align-items-center">
                    <h5>Add New Account</h5>
                    <select className="form-select w-auto" value={newAccount.type} onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value })}>
                      <option value="upi">UPI</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>

                  <form onSubmit={handleAddAccount} className="row g-3 mt-2">
                    {newAccount.type === "upi" && (
                      <>
                        <div className="col-md-6">
                          <input type="text" className="form-control" placeholder="UPI ID" value={newAccount.upiId} onChange={(e) => setNewAccount({ ...newAccount, upiId: e.target.value })} required />
                        </div>
                        <div className="col-md-6">
                          <input type="file" accept="image/*" className="form-control" onChange={(e) => setNewAccount({ ...newAccount, qrCode: e.target.files[0] })} />
                        </div>
                      </>
                    )}
                    {newAccount.type === "bank" && (
                      <>
                        <div className="col-md-4">
                          <input type="text" className="form-control" placeholder="Bank Name" value={newAccount.bankName} onChange={(e) => setNewAccount({ ...newAccount, bankName: e.target.value })} required />
                        </div>
                        <div className="col-md-4">
                          <input type="text" className="form-control" placeholder="Account Number" value={newAccount.accountNumber} onChange={(e) => setNewAccount({ ...newAccount, accountNumber: e.target.value })} required />
                        </div>
                        <div className="col-md-4">
                          <input type="text" className="form-control" placeholder="IFSC Code" value={newAccount.ifsc} onChange={(e) => setNewAccount({ ...newAccount, ifsc: e.target.value })} required />
                        </div>
                      </>
                    )}
                    <div className="col-12">
                      <button type="submit" className="btn btn-success">Add Account</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
